import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import axios, { AxiosError } from 'axios'
import { FormikValues } from 'formik'
import { Button, useEditProjectTopic, useShowMessage } from '@pepper/ui'

import EditTopicForm from '../EditTopicForm'

import { useGenerateBulkSeoGuidelines } from 'utils/hooks/useGenerateBulkSeoGuidelines'
import { useAddTopics } from 'utils/hooks/useAddTopics'

import type { Topic } from '@pepper/utils/src/types/seo'
import type { SuggestedTopic } from 'types/topic'

import s from './EditTopicModal.module.css'

type Props = {
  open: boolean
  companyId: string
  projectId: string
  domainRegion: string
  onClose: () => void
  selectedProjectTopic?: Topic
  selectedSuggestedTopic?: SuggestedTopic
  title?: string
  showAttachSeoGuidelines?: boolean
  onSuccess?: () => void
}

export const EditTopicModal: React.FC<Props> = ({
  open,
  companyId,
  projectId,
  domainRegion,
  onClose,
  selectedProjectTopic,
  selectedSuggestedTopic,
  onSuccess,
  title,
  showAttachSeoGuidelines = false,
}) => {
  const history = useHistory()
  const showMessage = useShowMessage()
  const [shouldAttachSeoGuidelines, setShouldAttachSeoGuidelines] =
    useState(true)

  const { mutateAsync: updateProjectTopic, isLoading: isUpdatingProjectTopic } =
    useEditProjectTopic()
  const { mutateAsync: addTopics, isLoading: isAdding } = useAddTopics()
  const { mutateAsync: generateBulkSeoGuidelines } =
    useGenerateBulkSeoGuidelines()

  const selectedTopic = selectedProjectTopic ?? selectedSuggestedTopic

  const initialValues = {
    name: selectedTopic?.name ?? '',
    primaryKeyword: selectedTopic?.keywords?.find(k => k.type === 'primary')
      ?.keyword,
    secondaryKeywords: selectedTopic?.keywords
      ?.filter(k => k.type === 'secondary')
      .map(k => k.keyword),
    shouldAttachSeoGuidelines: true,
  }

  const handleClose = () => {
    onClose()
    setShouldAttachSeoGuidelines(true)
  }

  const handleUpdateTopic = async (values: FormikValues) => {
    if (!selectedProjectTopic) return

    try {
      await updateProjectTopic({
        companyId,
        topic: values.name,
        topicId: selectedProjectTopic.id,
        primaryKeyword: values.primaryKeyword,
        secondaryKeywords: values.secondaryKeywords,
        createCrr: showAttachSeoGuidelines && values.shouldAttachSeoGuidelines,
      })
      handleClose()
      showMessage({ type: 'success', message: 'Topic updated successfully' })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const _error: AxiosError<{ description: string }> = error
        showMessage({
          type: 'error',
          message: _error.response?.data.description,
        })
      }
    }
  }

  const handleAddAndUpdate = async (values: FormikValues) => {
    if (!selectedSuggestedTopic) return
    const { status, data, error } = await addTopics({
      companyId,
      projectId,
      topicIds: [selectedSuggestedTopic.id],
    })
    onSuccess?.()
    if (status === 'success' && data && data.length > 0) {
      const { status: _status, message } = await updateProjectTopic({
        companyId,
        topic: values.name,
        topicId: data[0].id,
        primaryKeyword: values.primaryKeyword,
        secondaryKeywords: values.secondaryKeywords,
      })
      if (_status === 'success') {
        if (shouldAttachSeoGuidelines) {
          const seoGuidelineResponse = await generateBulkSeoGuidelines({
            companyId,
            topics: data.map(topic => ({
              topic_id: topic.id,
              topic: topic.name,
              region_code: domainRegion,
            })),
          })
          if (seoGuidelineResponse.status === 'error') {
            showMessage({
              type: 'error',
              message: seoGuidelineResponse.error,
            })
          }
        }
        showMessage({
          type: 'success',
          message: (
            <div className={s.toastMessage}>
              Topic added successfully
              <Button
                type="default"
                onClick={() => history.push(`/project/${projectId}/topics`)}
              >
                Go to project
              </Button>
            </div>
          ),
        })
        handleClose()
      } else {
        showMessage({ type: 'error', message })
      }
    } else {
      showMessage({ type: 'error', message: error })
    }
  }

  return (
    <EditTopicForm
      open={open}
      onClose={onClose}
      initialValues={initialValues}
      onSubmit={selectedSuggestedTopic ? handleAddAndUpdate : handleUpdateTopic}
      isLoading={isUpdatingProjectTopic || isAdding}
      isSuggestedTopic={!!selectedSuggestedTopic}
      title={title}
      showAttachSeoGuidelines={showAttachSeoGuidelines}
    />
  )
}
